/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, computed, observable, action, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import c from 'classnames';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

import { categories } from '../../models/studies/categories';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

const SELECTION_STORAGE_KEY = 'swb-study-selection-memory';

function readStoredSelection() {
  try {
    const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
    if (raw === null) return null; // first time — no key at all
    const parsed = JSON.parse(raw);
    // Must be an array of strings — anything else is treated as corrupt
    if (!Array.isArray(parsed) || !parsed.every(v => typeof v === 'string')) {
      localStorage.removeItem(SELECTION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    try { localStorage.removeItem(SELECTION_STORAGE_KEY); } catch (__) { /* noop */ }
    return null;
  }
}

function saveSelection(studyIds) {
  try {
    if (!Array.isArray(studyIds)) return;
    localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(studyIds));
  } catch (_) { /* noop */ }
}

// expected props
// - filesSelection (via injection)
// - studiesStoresMap (via injection)
// - userStore (via injection)
class StudySelectionGrid extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.openDataExpanded = false;
      this.autoSelectionApplied = false;
      this.openDataSearch = '';
    });
  }

  componentDidMount() {
    const allReady = _.every(categories, category => {
      const store = this.getStore(category);
      return !store || isStoreReady(store) || isStoreError(store);
    });

    if (allReady) {
      // Stores already loaded (e.g. from visiting Studies page) — apply immediately
      runInAction(() => { this.autoSelectionApplied = true; });
      this.applyAutoSelection();
    } else {
      // Stores need loading — componentDidUpdate will handle auto-selection once ready
      _.forEach(categories, category => {
        const store = this.getStore(category);
        if (store && !isStoreReady(store)) {
          swallowError(store.load());
        }
      });
    }
  }

  componentDidUpdate() {
    if (!this.autoSelectionApplied && this.allStoresReady) {
      runInAction(() => {
        this.autoSelectionApplied = true;
      });
      this.applyAutoSelection();
    }
  }

  componentWillUnmount() {
    // Save current selection when leaving the page
    const selection = this.props.filesSelection;
    const ids = [];
    selection.files.forEach(file => ids.push(file.id));
    saveSelection(ids);
  }

  get allStoresReady() {
    return _.every(categories, category => {
      const store = this.getStore(category);
      return !store || isStoreReady(store) || isStoreError(store);
    });
  }

  applyAutoSelection() {
    try {
      const selection = this.props.filesSelection;
      // Clear any stale selection before restoring from storage
      selection.cleanup();

      const allStudies = this.getAllSelectableStudies();
      const stored = readStoredSelection();

      if (stored !== null) {
        // Returning user — re-select only studies that are still available
        stored.forEach(id => {
          const study = allStudies.find(s => s.id === id);
          if (study && study.state.canSelect && !selection.hasFile(id)) {
            selection.setFile({ id: study.id, name: study.name, description: study.description, accessStatus: 'approved' });
          }
        });
      } else {
        // First time — auto-select personal study if available
        allStudies.forEach(study => {
          if (study.id.endsWith('-personal-study') && study.state.canSelect) {
            selection.setFile({ id: study.id, name: study.name, description: study.description, accessStatus: 'approved' });
          }
        });
      }
    } catch (_) {
      // If anything goes wrong, clear corrupt data and continue without auto-selection
      try { localStorage.removeItem(SELECTION_STORAGE_KEY); } catch (__) { /* noop */ }
    }
  }

  getAllSelectableStudies() {
    const studies = [];
    _.forEach(categories, category => {
      const store = this.getStore(category);
      if (store && isStoreReady(store)) {
        store.list.forEach(s => studies.push(s));
      }
    });
    return studies;
  }

  getStore(category) {
    return this.props.studiesStoresMap[category.id];
  }

  get isExternalUser() {
    return _.get(this.props.userStore, 'user.isExternalUser', true);
  }

  get licenseStudies() {
    const orgStore = this.getStore(categories.organization);
    if (orgStore && isStoreReady(orgStore)) {
      return orgStore.list.filter(s => s.isLicenseStudy);
    }
    return [];
  }

  get myDataStudies() {
    const studies = [];
    // Personal studies (unless external user)
    if (!this.isExternalUser) {
      const myStore = this.getStore(categories.myStudies);
      if (myStore && isStoreReady(myStore)) {
        myStore.list.forEach(s => studies.push(s));
      }
    }
    // Organization studies (excluding licenses)
    const orgStore = this.getStore(categories.organization);
    if (orgStore && isStoreReady(orgStore)) {
      orgStore.list.forEach(s => { if (!s.isLicenseStudy) studies.push(s); });
    }
    return _.orderBy(studies, [s => (s.isOrganizationStudy ? 1 : 0), 'createdAt', 'name'], ['asc', 'desc', 'asc']);
  }

  get openDataStudies() {
    const store = this.getStore(categories.openData);
    if (store && isStoreReady(store)) return store.list;
    return [];
  }

  get isLoading() {
    return _.some(categories, category => {
      const store = this.getStore(category);
      return store && isStoreLoading(store);
    });
  }

  get hasError() {
    return _.find(categories, category => {
      const store = this.getStore(category);
      return store && isStoreError(store);
    });
  }

  toggleOpenData = () => {
    this.openDataExpanded = !this.openDataExpanded;
  };

  handleOpenDataSearch = (e) => {
    this.openDataSearch = e.target.value;
  };

  handleToggleStudy = study => {
    const selection = this.props.filesSelection;
    if (selection.hasFile(study.id)) {
      selection.deleteFile(study.id);
    } else {
      const { id, name, description } = study;
      selection.setFile({ id, name, description, accessStatus: 'approved' });
    }
  };

  getStudyIcon(study) {
    if (study.isOpenDataStudy) return 'globe';
    if (study.isLicenseStudy) return 'key';
    if (study.isOrganizationStudy) return 'users';
    return 'folder';
  }

  render() {
    if (this.isLoading) {
      return <BasicProgressPlaceholder className="mt2" />;
    }

    const errorCategory = this.hasError;
    if (errorCategory) {
      const store = this.getStore(errorCategory);
      return <ErrorBox error={store.error} className="mt2" />;
    }

    return (
      <div className="study-select-container">
        {this.renderLicensesSection()}
        {this.renderMyDataSection()}
        {this.renderOpenDataSection()}
      </div>
    );
  }

  renderLicensesSection() {
    const studies = this.licenseStudies;
    if (studies.length === 0) return null;

    const selection = this.props.filesSelection;
    const selectedCount = studies.filter(s => selection.hasFile(s.id)).length;

    return (
      <div className="study-select-section">
        <div className="study-select-section-header">
          <Icon name="key" style={iconStyle} />
          <span>Licenses</span>
          <span className="study-select-section-count">{selectedCount}/{studies.length}</span>
        </div>
        <div className="study-select-grid">
          {studies.map(study => this.renderTile(study))}
        </div>
      </div>
    );
  }

  renderMyDataSection() {
    const studies = this.myDataStudies;

    const selection = this.props.filesSelection;
    const selectedCount = studies.filter(s => selection.hasFile(s.id)).length;

    return (
      <div className="study-select-section">
        <div className="study-select-section-header">
          <Icon name="database" style={iconStyle} />
          <span>Studies</span>
          <span className="study-select-section-count">{selectedCount}/{studies.length}</span>
        </div>
        {studies.length === 0 ? (
          this.renderEmptySection('No personal or shared studies available.')
        ) : (
          <div className="study-select-grid">
            {studies.map(study => this.renderTile(study))}
          </div>
        )}
      </div>
    );
  }

  renderOpenDataSection() {
    const allStudies = this.openDataStudies;
    const expanded = this.openDataExpanded;
    const searchTerm = this.openDataSearch.toLowerCase().trim();
    const studies = searchTerm
      ? allStudies.filter(s => (s.name || '').toLowerCase().includes(searchTerm))
      : allStudies;

    const selection = this.props.filesSelection;
    const selectedCount = allStudies.filter(s => selection.hasFile(s.id)).length;

    return (
      <div className="study-select-section">
        <button className="study-select-section-header study-select-section-header-collapsible" onClick={this.toggleOpenData}>
          <Icon name="globe" style={iconStyle} />
          <span>Open Data</span>
          <span className="study-select-section-count">{selectedCount}/{allStudies.length}</span>
          <Icon name={expanded ? 'chevron down' : 'chevron right'} className="study-select-section-chevron" style={iconStyle} />
        </button>
        {expanded && (
          <>
            <div style={{ position: 'relative', margin: '12px 0' }}>
              <Icon name="search" style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                margin: 0,
                lineHeight: 1,
                height: '1em',
                fontSize: '0.9em',
                pointerEvents: 'none',
              }} />
              <input
                type="text"
                placeholder="Search open data by name..."
                value={this.openDataSearch}
                onChange={this.handleOpenDataSearch}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 38px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.9em',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
                onFocus={e => { e.target.style.borderColor = 'var(--rmit-blue-50)'; e.target.style.boxShadow = '0 0 0 2px rgba(0, 0, 84, 0.08)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            {studies.length === 0 ? (
              this.renderEmptySection(searchTerm ? `No studies matching "${this.openDataSearch}".` : 'No open data studies available.')
            ) : (
              <div className="study-select-grid">
                {studies.map(study => this.renderTile(study))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  renderTile(study) {
    const selection = this.props.filesSelection;
    const isSelected = selection.hasFile(study.id);
    const canSelect = study.state.canSelect;
    const icon = this.getStudyIcon(study);

    return (
      <div
        key={study.id}
        className={c('study-select-tile', {
          selected: isSelected,
          disabled: !canSelect,
        })}
        onClick={canSelect ? () => this.handleToggleStudy(study) : undefined}
        role="button"
        tabIndex={canSelect ? 0 : -1}
        onKeyDown={canSelect ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleToggleStudy(study); } } : undefined}
      >
        <div className="study-select-tile-top">
          <span className={`study-pill ${this.getPillClass(study)}`}>
            <Icon name={icon} style={iconStyle} /> {this.getPillLabel(study)}
          </span>
          {isSelected && (
            <div className="study-select-tile-check">
              <Icon name="check" style={iconStyle} />
            </div>
          )}
        </div>
        <div className="study-select-tile-name">{study.name}</div>
        <div className="study-select-tile-id">{study.id}</div>
        {study.description && (
          <div className="study-select-tile-desc">{study.description}</div>
        )}
        {!canSelect && (
          <div style={{ marginTop: '4px' }}>
            <span className="study-select-tile-status">{study.state.display}</span>
          </div>
        )}
      </div>
    );
  }

  getPillClass(study) {
    if (study.isOpenDataStudy) return 'study-pill-open';
    if (study.isLicenseStudy) return 'study-pill-license';
    if (study.isOrganizationStudy) return 'study-pill-shared';
    return 'study-pill-personal';
  }

  getPillLabel(study) {
    if (study.isOpenDataStudy) return 'Open Data';
    if (study.isLicenseStudy) return 'License';
    if (study.isOrganizationStudy) return 'Shared';
    return 'Personal';
  }

  renderPill(study) {
    if (study.isOpenDataStudy) {
      return <span className="study-pill study-pill-open">Open Data</span>;
    }
    if (study.isLicenseStudy) {
      return <span className="study-pill study-pill-license">License</span>;
    }
    const isShared = study.isOrganizationStudy;
    const label = isShared ? 'Shared' : 'Personal';
    const pillClass = isShared ? 'study-pill study-pill-shared' : 'study-pill study-pill-personal';
    return <span className={pillClass}>{label}</span>;
  }

  renderEmptySection(message) {
    return (
      <div className="study-select-empty">
        <Icon name="inbox" style={iconStyle} />
        <span>{message}</span>
      </div>
    );
  }
}

decorate(StudySelectionGrid, {
  openDataExpanded: observable,
  autoSelectionApplied: observable,
  openDataSearch: observable,
  toggleOpenData: action,
  handleToggleStudy: action,
  handleOpenDataSearch: action,
  isExternalUser: computed,
  licenseStudies: computed,
  myDataStudies: computed,
  openDataStudies: computed,
  isLoading: computed,
  hasError: computed,
  allStoresReady: computed,
});

export default inject('filesSelection', 'studiesStoresMap', 'userStore')(observer(StudySelectionGrid));
